/**
 * BytesCell Component
 * 
 * Renders Parse Bytes values with:
 * - Size display: "Bytes (1.2 KB)"
 * - Click to expand and view hex/base64
 */

import { CellRendererProps, ParseBytes } from '../../../../../types/AppPanel';

export default function BytesCell({ 
  value, 
  onExpand 
}: CellRendererProps<ParseBytes | null | undefined>) {
  // Handle null/undefined
  if (value === null) {
    return <span className="text-white/30 italic">(null)</span>;
  }
  if (value === undefined) {
    return <span className="text-white/30 italic">(undefined)</span>;
  }

  // Validate Bytes structure
  if (typeof value !== 'object' || typeof value.base64 !== 'string') {
    return <span className="text-white/30 italic">(invalid bytes)</span>;
  }

  // Calculate size from base64
  const base64 = value.base64;
  const byteSize = Math.ceil((base64.length * 3) / 4);
  const sizeDisplay = formatBytes(byteSize);

  return (
    <button
      onClick={onExpand}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full 
                 bg-slate-500/20 text-slate-400 text-xs font-medium
                 hover:bg-slate-500/30 transition-colors cursor-pointer"
      title={`Binary data: ${sizeDisplay}`}
    >
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
      Bytes ({sizeDisplay})
    </button>
  );
}

/**
 * Format byte size to human-readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

